// Cart-side subscription/selling-plan widget: toggles a line item between one-time and a
// Shopify selling plan. /cart/change.js can't alter a line's selling plan, so every switch
// is "add the new line, then zero out the old one".
class SubscriptionWidget extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.lineKey = this.getAttribute("data-line-key");
    this.currentSellingPlanId = this.getAttribute("data-current-selling-plan-id") || null;

    try {
      this.availablePlans = JSON.parse(this.getAttribute("data-available-plans") || "[]");
    } catch (error) {
      this.availablePlans = [];
    }

    this.toggleInput = this.querySelector('input[type="checkbox"].switch');
    this.body = this.querySelector(".subscription-widget__body");
    this.frequencyList = this.querySelector(".subscription-widget__frequency-list");

    this.toggleInput?.addEventListener("change", this.onToggleChange.bind(this), { signal });
    this.frequencyList?.addEventListener("click", this.onFrequencyButtonClick.bind(this), { signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  onToggleChange(event) {
    if (this.busy) return;

    if (event.target.checked) {
      const targetPlan = this.availablePlans[0];

      if (!targetPlan) {
        event.target.checked = false;
        return;
      }

      // Cast to string: every comparison against this.currentSellingPlanId assumes a string.
      this.switchPlan(String(targetPlan.id));
    } else {
      this.switchPlan(null);
    }
  }

  onFrequencyButtonClick(event) {
    if (this.busy) return;

    const button = event.target.closest(".subscription-widget__frequency-button");
    if (!button) return;

    const sellingPlanId = button.getAttribute("data-selling-plan-id");
    if (sellingPlanId === this.currentSellingPlanId) return;

    this.switchPlan(sellingPlanId);
  }

  setBusy(isBusy) {
    this.busy = isBusy;
    this.classList.toggle("is-loading", isBusy);

    if (this.toggleInput) {
      this.toggleInput.disabled = isBusy;
    }

    this.querySelectorAll(".subscription-widget__frequency-button").forEach((button) => {
      button.disabled = isBusy;
    });
  }

  showError(message) {
    this.querySelector('[role="alert"]')?.remove();
    this.insertAdjacentHTML(
      "beforeend",
      `<p class="subscription-widget__error text-xs text-error" role="alert">${message}</p>`
    );
  }

  // Syncs the switch/frequency-buttons/body visibility to this.currentSellingPlanId - used to
  // revert on failure and to reflect success, so this instance stays correct even if the
  // cart-drawer section swap that normally replaces it doesn't land in time.
  syncUiToCurrentState() {
    if (this.toggleInput) {
      this.toggleInput.checked = this.currentSellingPlanId != null;
    }

    if (this.body) {
      this.body.hidden = this.currentSellingPlanId == null;
    }

    this.querySelectorAll(".subscription-widget__frequency-button").forEach((button) => {
      const isSelected = button.getAttribute("data-selling-plan-id") === this.currentSellingPlanId;
      button.classList.toggle("is-selected", isSelected);
      button.setAttribute("aria-pressed", isSelected ? "true" : "false");
    });
  }

  async switchPlan(newSellingPlanId) {
    this.setBusy(true);
    this.querySelector('[role="alert"]')?.remove();
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
      if (!cartResponse.ok) throw new Error("cart_fetch_failed");

      const cart = await cartResponse.json();
      const currentLine = cart.items.find((item) => item.key === this.lineKey);
      if (!currentLine) throw new Error("line_not_found");

      const addPayload = {
        id: currentLine.variant_id,
        quantity: currentLine.quantity,
        properties: currentLine.properties || {}
      };

      if (newSellingPlanId) {
        addPayload.selling_plan = Number(newSellingPlanId);
      }

      const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(addPayload)
      });

      const addedItem = await addResponse.json();

      if (!addResponse.ok) {
        throw new Error(addedItem?.description || "add_failed");
      }

      const previousLineKey = this.lineKey;

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      let finalCart;

      if (addedItem.key !== previousLineKey) {
        const removeResponse = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: previousLineKey, quantity: 0, sections: sectionsToBundle.join(",") })
        });

        if (!removeResponse.ok) {
          // Added but couldn't remove the old line - cart is in a real (if messy) state, so force
          // a full resync rather than leave a stale UI up.
          document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          this.showError(window.themeVariables.strings.subscriptionUpdateError);
          this.setBusy(false);
          return;
        }

        finalCart = await removeResponse.json();
      } else {
        const refetchResponse = await fetch(`${Shopify.routes.root}cart.js?sections=${sectionsToBundle.join(",")}`);
        finalCart = await refetchResponse.json();
      }

      // This instance now represents a different cart line (its old key was just zeroed out) -
      // keep it correct even if the section swap below doesn't land immediately.
      this.lineKey = addedItem.key;
      this.currentSellingPlanId = newSellingPlanId;
      this.syncUiToCurrentState();

      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));

      if (window.themeVariables.settings.pageType === "cart") {
        window.location.reload();
        return;
      }

      this.setBusy(false);

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "line-item:change", cart: finalCart } })
      );
    } catch (error) {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      this.syncUiToCurrentState();
      this.showError(window.themeVariables.strings.subscriptionUpdateError);
      this.setBusy(false);
    }
  }
}

if (!window.customElements.get("subscription-widget")) {
  window.customElements.define("subscription-widget", SubscriptionWidget);
}
